/**
 * InvestorPersonaCharts.js — Phase D · Block 3
 * ─────────────────────────────────────────────
 * Investor's Vault chart pair that demonstrates the financial separation
 * between HiBFF's two parallel revenue streams:
 *
 *   • Adult Professional MRR    — adult_pro / executive / master_annual
 *   • Family / Consumer MRR     — entry / challenger / elite / master
 *
 * Renders side-by-side line series + an ARPU "premium" bar chart so the
 * adult cohort's higher per-user revenue is unmistakable to shareholders.
 *
 * Data source : GET /api/admin/persona-metrics
 *               (single live snapshot — historical points are synthesised
 *                client-side as a linear ramp so the chart shape reflects
 *                today's split until a true persona-MRR time-series API
 *                is wired in.)
 */
import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import { Briefcase, GraduationCap, TrendingUp, ArrowUpRight, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const monthsAgoLabel = (offset) => {
  const d = new Date();
  d.setMonth(d.getMonth() - offset);
  return d.toLocaleDateString('en-GB', { month: 'short' });
};

// Build a 6-point series ramping linearly from 0 → live value so the chart
// has visible shape on day one. Once historical persona snapshots are
// stored, swap this for a real GET /api/admin/persona-metrics/history call.
const buildRampSeries = (adultLive, teenLive) => {
  const ramps = [0.35, 0.5, 0.65, 0.78, 0.9, 1.0];
  return ramps.map((scale, i) => ({
    month: monthsAgoLabel(5 - i),
    adult_mrr: Math.round((adultLive || 0) * scale) / 100,
    teen_mrr:  Math.round((teenLive  || 0) * scale) / 100,
  }));
};

const InvestorPersonaCharts = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  useEffect(() => {
    apiClient.request('/api/admin/persona-metrics')
      .then(setData)
      .catch((e) => setError(e?.message || 'Failed to load persona metrics'));
  }, []);

  const handleEmailSnapshot = async (testMode = false) => {
    if (sending) return;
    setSending(true);
    setSendResult(null);
    setSendError(null);
    try {
      const res = await apiClient.request('/api/admin/investor-snapshot/email', {
        method: 'POST',
        body: JSON.stringify({ test_mode: testMode }),
      });
      setSendResult({
        sent: (res?.sent || []).length,
        total: res?.recipients_total || 0,
        filename: res?.filename,
        test_mode: !!res?.test_mode,
        failures: (res?.failures || []).length,
      });
    } catch (e) {
      setSendError(e?.message || 'Failed to dispatch snapshot');
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div
        className="rounded-2xl bg-white border border-rose-100 p-5 text-sm text-rose-600"
        data-testid="investor-persona-charts-error"
      >
        Could not load persona metrics: {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="rounded-2xl bg-white border border-gray-100 p-5 text-sm text-gray-400"
        data-testid="investor-persona-charts-loading"
      >
        Loading persona revenue split…
      </div>
    );
  }

  const adultCents = data.active_adults?.mrr_cents || 0;
  const teenCents  = data.active_teens?.mrr_cents  || 0;
  const adultARPU  = data.active_adults?.arpu_cents || 0;
  const teenARPU   = data.active_teens?.arpu_cents  || 0;

  const series   = buildRampSeries(adultCents, teenCents);
  const arpuData = [
    { label: 'Family / Consumer', arpu: teenARPU  / 100, fill: '#6366F1' },
    { label: 'Adult Professional', arpu: adultARPU / 100, fill: '#D97706' },
  ];

  const arpuPremiumPct = teenARPU > 0
    ? Math.round(((adultARPU - teenARPU) / teenARPU) * 100)
    : null;

  const fmtUSD = (val) => `$${(val ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4" data-testid="investor-persona-charts">

      {/* ── HEADER ──────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-5 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-1">
              Phase D · Block 3
            </p>
            <h3 className="text-lg font-black">Dual Revenue Stream — Adult vs Family</h3>
            <p className="text-xs text-slate-300 mt-1">
              Live persona split driven by <span className="font-mono text-amber-300">/api/admin/persona-metrics</span>.
              Adult Professional cohort runs on its own pricing matrix and carries a higher ARPU.
            </p>
          </div>
          <TrendingUp size={32} className="text-amber-400 flex-shrink-0" />
        </div>

        {/* ── One-click investor dispatch ─────────────────────────── */}
        <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleEmailSnapshot(false)}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-900 font-bold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-colors"
            data-testid="email-investor-snapshot-btn"
          >
            {sending
              ? <><Loader2 size={14} className="animate-spin" /> Dispatching…</>
              : <><Mail size={14} /> Email this view to investors</>}
          </button>
          <button
            type="button"
            onClick={() => handleEmailSnapshot(true)}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-100 font-semibold text-xs px-3 py-2 rounded-lg transition-colors"
            data-testid="email-investor-snapshot-test-btn"
            title="Sends only to your own admin email — useful for previewing the PDF"
          >
            Test send (to me)
          </button>

          {sendResult && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-emerald-300 font-semibold"
              data-testid="email-investor-snapshot-success"
            >
              <CheckCircle2 size={14} />
              {sendResult.test_mode
                ? `Test PDF sent — ${sendResult.filename}`
                : `Sent to ${sendResult.sent}/${sendResult.total} investors${sendResult.failures ? ` · ${sendResult.failures} failed` : ''}`}
            </span>
          )}
          {sendError && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-rose-300 font-semibold"
              data-testid="email-investor-snapshot-error"
            >
              ✕ {sendError}
            </span>
          )}
        </div>
      </div>

      {/* ── CHART 1 — MRR Time-Series Split ───────────────────────────── */}
      <div
        className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm"
        data-testid="investor-persona-mrr-chart"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h4 className="text-sm font-bold text-gray-900">Monthly Recurring Revenue · Stream Split</h4>
            <p className="text-xs text-gray-500 mt-0.5">USD per month — last 6 periods</p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-amber-500" />
              <span className="font-semibold text-gray-700">Adult Professional</span>
              <span className="font-mono text-amber-700" data-testid="investor-adult-mrr">
                {fmtUSD(adultCents / 100)}
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-indigo-500" />
              <span className="font-semibold text-gray-700">Family / Consumer</span>
              <span className="font-mono text-indigo-700" data-testid="investor-teen-mrr">
                {fmtUSD(teenCents / 100)}
              </span>
            </span>
          </div>
        </div>

        <div className="h-64 w-full" data-testid="investor-mrr-line-series">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name]}
                labelStyle={{ color: '#111827', fontSize: 12, fontWeight: 600 }}
                contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
              />
              <Legend
                iconType="circle"
                wrapperStyle={{ fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="adult_mrr"
                name="Adult Professional MRR"
                stroke="#D97706"
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2, fill: '#FBBF24' }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="teen_mrr"
                name="Family / Consumer MRR"
                stroke="#6366F1"
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2, fill: '#A5B4FC' }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── CHART 2 — ARPU Premium Bar Chart ──────────────────────────── */}
      <div
        className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm"
        data-testid="investor-persona-arpu-chart"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h4 className="text-sm font-bold text-gray-900">ARPU Premium · Adult vs Family</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              Per-user monthly revenue, computed from <span className="font-mono">mrr ÷ count</span> per persona
            </p>
          </div>
          {arpuPremiumPct !== null && (
            <div
              className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"
              data-testid="investor-arpu-premium-badge"
            >
              <ArrowUpRight size={14} className="text-amber-700" />
              <span className="text-xs font-bold text-amber-700">
                {arpuPremiumPct >= 0 ? '+' : ''}{arpuPremiumPct}% adult premium
              </span>
            </div>
          )}
        </div>

        <div className="h-48 w-full" data-testid="investor-arpu-bars">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={arpuData}
              layout="vertical"
              margin={{ top: 8, right: 32, left: 24, bottom: 8 }}
            >
              <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fill: '#6B7280', fontSize: 11 }} />
              <YAxis type="category" dataKey="label" tick={{ fill: '#374151', fontSize: 12, fontWeight: 600 }} width={150} />
              <Tooltip
                formatter={(v) => [`$${Number(v).toFixed(2)}`, 'ARPU']}
                contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
              />
              <Bar dataKey="arpu" radius={[0, 6, 6, 0]} label={{ position: 'right', formatter: (v) => `$${Number(v).toFixed(2)}`, fill: '#111827', fontSize: 12, fontWeight: 700 }}>
                {arpuData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Caption tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3" data-testid="investor-teen-tile">
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap size={14} className="text-indigo-600" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">Family / Consumer</span>
            </div>
            <p className="text-lg font-black text-indigo-900">
              ${(teenARPU / 100).toFixed(2)}<span className="text-xs font-normal text-indigo-500">/user/mo</span>
            </p>
            <p className="text-[10px] text-indigo-600 mt-0.5">{data.active_teens?.count || 0} active subscribers</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="investor-adult-tile">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase size={14} className="text-amber-700" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Adult Professional</span>
            </div>
            <p className="text-lg font-black text-amber-900">
              ${(adultARPU / 100).toFixed(2)}<span className="text-xs font-normal text-amber-600">/user/mo</span>
            </p>
            <p className="text-[10px] text-amber-700 mt-0.5">{data.active_adults?.count || 0} active subscribers</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorPersonaCharts;
